import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AboutPage() {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function fetchHistory() {
      try {
        const { data } = await axios.get(`${API}/api/history`);
        setHistory(data);
      } catch (error) {
        console.error('Failed to fetch history:', error);
      }
    }
    fetchHistory();
  }, []);

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">About Us</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Private Capital, Long-Term Vision
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            BNDV Capital is a Single Family Office founded in London in 2014 by David Dolce. 
            Through our investment vehicle Dolchester Holding, we build and nurture businesses 
            across Europe with a commitment to long-term value creation.
          </p>
        </motion.div>
      </div>

      {/* Vision Section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop"
              alt="Corporate office"
              className="w-full h-[400px] object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center"
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight mb-6">
              Our Structure
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>BNDV Capital</strong> serves as our Single Family Office, providing strategic direction 
              and oversight for all investment activities. Through <strong>Dolchester Holding</strong>, our 
              dedicated investment vehicle, we execute our long-term investment strategy across Europe.
            </p>
            <p className="text-slate-600 leading-relaxed">
              This structure combines the agility of a family office with the operational rigor of a 
              professional investment platform, allowing us to move quickly on opportunities while 
              maintaining disciplined governance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Our Values</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                title: 'Long-Term Orientation',
                description: 'We invest with a generational perspective, supporting our companies through cycles and allowing strategies to fully develop.'
              },
              {
                title: 'Partnership',
                description: 'We work alongside management teams as trusted partners, providing capital, expertise, and a stable ownership structure.'
              },
              {
                title: 'Responsibility',
                description: 'We are committed to sustainable business practices and creating positive impact for all stakeholders.'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <h3 className="font-medium text-[#0A192F] mb-4">{value.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Our Journey</p>
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">History</h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 hidden md:block" />

            <div className="space-y-12">
              {history.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative grid md:grid-cols-2 gap-8 ${index % 2 === 0 ? '' : 'md:direction-rtl'}`}
                >
                  <div className={`${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:order-2 md:text-left md:pl-12'}`}>
                    <div className="font-serif text-4xl text-[#0A192F]/20 mb-2">{item.year}</div>
                    <h3 className="font-medium text-[#0A192F] mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                  <div className={`hidden md:block ${index % 2 === 0 ? 'md:order-2' : ''}`}>
                    {/* Timeline dot */}
                    <div className="absolute left-1/2 top-4 -translate-x-1/2 w-3 h-3 bg-[#0A192F] rounded-full" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Family Quote */}
      <section className="py-24 bg-[#0A192F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <blockquote className="font-serif text-2xl lg:text-3xl text-white leading-relaxed mb-8">
              "We build to last. Every investment decision is guided by the question: will this create 
              value for the next generation?"
            </blockquote>
            <cite className="text-slate-400 text-sm uppercase tracking-wider">
              — David Dolce, President & CEO
            </cite>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
